import React from "react";
import { Link } from "react-router-dom";

import AfriMedLandingShell from "@/components/layout/AfriMedLandingShell.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { unsubscribeCommunicationEmail } from "@/services/communicationPreferencesService.js";

function readToken() {
  if (typeof window === "undefined") return "";
  try {
    return String(new URLSearchParams(window.location.search || "").get("token") || "").trim();
  } catch {
    return "";
  }
}

export default function CommunicationUnsubscribePage() {
  const { t } = useLanguage();
  const token = React.useMemo(() => readToken(), []);
  const [status, setStatus] = React.useState("processing");
  const [error, setError] = React.useState("");
  const [email, setEmail] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function runUnsubscribe() {
      if (!token) {
        if (alive) {
          setStatus("error");
          setEmail("");
          setError(t("communications.unsubscribe.invalidLink", "This unsubscribe link is invalid or incomplete."));
        }
        return;
      }

      try {
        setStatus("processing");
        setEmail("");
        setError("");
        const response = await unsubscribeCommunicationEmail(token);
        if (!alive) return;
        setEmail(String(response?.email || "").trim());
        setStatus("success");
      } catch (nextError) {
        if (!alive) return;
        setStatus("error");
        setEmail("");
        setError(nextError?.message || t("communications.unsubscribe.error", "AfriMed™ could not complete this unsubscribe request."));
      }
    }

    void runUnsubscribe();
    return () => {
      alive = false;
    };
  }, [token, t]);

  const isSuccess = status === "success";
  const isProcessing = status === "processing";
  const title = isSuccess
    ? t("communications.unsubscribe.title", "Email unsubscribed")
    : isProcessing
      ? t("communications.unsubscribe.processingTitle", "Processing unsubscribe")
      : t("communications.unsubscribe.invalidTitle", "Invalid communication link");
  const subtitle = isSuccess
    ? t("communications.unsubscribe.subtitle", "AfriMed™ has updated this communication preference.")
    : isProcessing
      ? t("communications.unsubscribe.processingSubtitle", "Please wait while AfriMed™ verifies this link.")
      : t("communications.unsubscribe.invalidSubtitle", "This request could not be completed.");

  return (
    <AfriMedLandingShell
      title={title}
      subtitle={subtitle}
      contentClassName="max-w-[560px]"
    >
      {isProcessing ? (
        <div className="w-full text-center text-body-large text-[rgb(var(--clr-on-surface-variant))]">
          {t("communications.unsubscribe.processing", "Processing your request...")}
        </div>
      ) : status === "error" ? (
        <div className="rounded-[16px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
          {error}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-[16px] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-[rgb(var(--clr-on-surface))]">
            {t("communications.unsubscribe.noticePrefix", "Non-critical AfriMed™ emails have been turned off for")}{" "}
            <strong>{email}</strong>.{" "}
            {t("communications.unsubscribe.noticeSuffix", "Security and sign-in emails remain enabled.")}
          </div>

          <Link
            to={`/communications/preferences?token=${encodeURIComponent(token)}`}
            className="inline-flex w-full items-center justify-center rounded-[14px] bg-[rgb(var(--afrimed-primary))] px-5 py-3 text-title-medium text-[rgb(var(--afrimed-on-primary))] no-underline"
          >
            {t("communications.unsubscribe.managePreferences", "Manage preferences")}
          </Link>
        </div>
      )}
    </AfriMedLandingShell>
  );
}
